import { useCallback } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import type { VendorMessageSection } from "@b2bportal/ground-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  CarShopSection,
  useCarPickupDropoffLocation,
  CarCheckoutPickupDropoff,
} from "@tenant/cars";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  Map,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CarPickupDropoffLocation.module.scss";
import type { VehicleAvailabilityWithSupplier } from "~core-cars/store";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";

const COMPONENT_KEY = CoreCarsComponent.CarPickupDropoffLocation;

export type CarPickupDropoffLocationType = {
  car: VehicleAvailabilityWithSupplier;
  pickUpDirections?: VendorMessageSection;
} & PlatformComponentProps;
export const CarPickupDropoffLocation = (
  props: CarPickupDropoffLocationType
) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { className, children } = props;
  const { context } = useCarPickupDropoffLocation(props);

  const { t } = useI18nContext();

  const MapMarker = useCallback(() => {
    return (
      <Icon
        className={cn("map-marker")}
        iconName={IconNameEnum.ui__mapMarker}
      />
    );
  }, [cn]);

  return (
    <CarShopSection
      className={clsx(block, className)}
      title={t("core-cars.locationCard.title")}
    >
      <div className={cn("content")}>
        <div
          className={cn("map-container", {
            hasDifferentDropoff: context.map.dropOff != null,
          })}
        >
          <Map
            className={cn("map")}
            centroid={context.map.pickup}
            MapMarker={MapMarker}
            points={[
              {
                id: "",
                data: null,
                label: props.car.supplierName ?? props.car.supplier.name,
                coordinates: context.map.pickup,
              },
            ]}
          />

          {context.map.dropOff != null ? (
            <Map
              className={cn("map")}
              centroid={context.map.dropOff}
              MapMarker={MapMarker}
              points={[
                {
                  id: "",
                  data: null,
                  label: props.car.supplierName ?? props.car.supplier.name,
                  coordinates: context.map.dropOff,
                },
              ]}
            />
          ) : null}
        </div>
        <CarCheckoutPickupDropoff
          className={cn("pickup-dropoff")}
          titles={{
            pickupTitle: t("core-cars.locationCard.pickupTitle"),
            dropoffTitle: t("core-cars.locationCard.dropoffTitle"),
          }}
          addresses={{
            pickupAddress: context.pickupAddress,
            dropoffAddress: context.dropoffAddress,
          }}
          dateRange={{
            pickupDate: context.pickUpTime,
            dropoffDate: context.dropoffTime,
          }}
        />

        {context.pickUpDirections?.details?.[0]?.text ? (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                buttonType={ButtonTypeEnum.Link}
                className={cn("location-details-button")}
              >
                {t("core-cars.locationCard.locationDetailsLink")}
              </Button>
            </DialogTrigger>
            <DialogContent title={context.pickUpDirections.details[0]?.title}>
              <span
                dangerouslySetInnerHTML={{
                  __html: context.pickUpDirections.details[0]?.text ?? "",
                }}
              ></span>
            </DialogContent>
          </Dialog>
        ) : null}

        {children}
      </div>
    </CarShopSection>
  );
};
